import _ from "lodash";
import axios from "axios";
import uuidv4 from "uuid/v4";

import strings from "../resources/strings";
import appCfg from "../config";
import C from "../constants";

import axiosHelper from "./util/axiosHelper";
import { getAnalysisTypes, getArgusAnalysis } from "../helpers/analysis";
var weberUrl = process.env.REACT_APP_WEBER_URL;
var detectAsyncUrl = process.env.REACT_APP_POPPERNAUT_URL + "/detect_async";
var detectFacesUrl = process.env.REACT_APP_POPPERNAUT_URL + "/detect_faces";

var axiosInstance;
var uploadProgress = {};
const backgroundTaskSize = 251473; // use this as an arbitrary size for background tasks to finish so progress bar will show some progress for background tasks

resetUploadProgress();

resetInstance();
axiosHelper.initializeInterceptors(axiosInstance);


function resetInstance() {
    axiosInstance = axios.create({
        baseURL: appCfg.serverUrl,
        timeout: appCfg.serverTimeout,
        headers: {
            "Content-Type": "application/json"
        },
        crossdomain: true,
        withCredentials: true,
        maxContentLength: 500 * 1024 * 1024, //500MB
        onUploadProgress: fileUploadProgress
    });
}

function updateProgressTotals(total) {
    uploadProgress.actualTotalToLoad += total;
    let newTotal = { total: total, loaded: 0 };
    uploadProgress.totals.push(newTotal);
    return newTotal;
}

function findUploadProgressTotal(progressEvent) {
    console.log(JSON.stringify(uploadProgress));
    for (let i = 0; i < uploadProgress.totals.length; i++) {
        let t = uploadProgress.totals[i];
        if (progressEvent.total === t.total) {
            return t;
        }
    }

    return updateProgressTotals(progressEvent.total);
}

function updateUploadProgress(loaded, total) {
    let totalToLoad = uploadProgress.totalToLoad > uploadProgress.actualTotalToLoad
            ? uploadProgress.totalToLoad : uploadProgress.actualTotalToLoad;
    let percentage = parseInt((uploadProgress.totalLoaded / totalToLoad) * 100, 10);
    console.log(uploadProgress.totalLoaded + "/" + totalToLoad + "  " + percentage + "%");
    if (uploadProgress.callbackFunction) {
        uploadProgress.callbackFunction(percentage < 100 ? percentage : 100, uploadProgress.totalLoaded,
            totalToLoad, loaded, total);
    }
    else {
        uploadProgress.callbackFunctionArgs = {
            percentage: percentage,
            totalLoaded: uploadProgress.totalLoaded,
            totalToLoad: totalToLoad
        };
    }
}

function fileUploadProgress(progressEvent) {
    if (uploadProgress.totalToLoad === 0 || !progressEvent.lengthComputable) {
        return;
    }

    console.debug("loaded: " + progressEvent.loaded + "  total: " + progressEvent.total +
            " length computable: " + progressEvent.lengthComputable);

    if (uploadProgress.callbackFunction) {
        let currentTotal = findUploadProgressTotal(progressEvent);
        let difference = progressEvent.loaded - currentTotal.loaded;
        uploadProgress.totalLoaded += difference;
        currentTotal.loaded = progressEvent.loaded;

        updateUploadProgress(progressEvent.loaded, progressEvent.total);
    }
}

function setUploadProgressFunction(func) {
    uploadProgress.callbackFunction = func;
    if (uploadProgress.callbackFunctionArgs) {
        let args = uploadProgress.callbackFunctionArgs;
        uploadProgress.callbackFunction(args.percentage, args.totalLoaded, args.totalToLoad);
        delete uploadProgress.callbackFunctionArgs;
    }
}

function resetUploadProgress() {
    // don't reset the callbackFunction
    uploadProgress.totals = [];
    uploadProgress.totalToLoad = 0;
    uploadProgress.actualTotalToLoad = 0;
    uploadProgress.totalLoaded = 0;
}

var loginWithRequestId = function(requestId) {
    resetUploadProgress();
    return axiosHelper.postRequest(axiosInstance, "/users/loginWithRequestId", { requestId: requestId }).then(response => {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + _.get(response, 'accessToken')
        return response;
    });
};

var loggedInWithRequestId = function() {
    return axiosHelper.getRequest(axiosInstance, "/users/loggedInWithRequestId");
};

var loginWithWorkflow = function(referenceId, organization) {
    resetUploadProgress();
    return axiosHelper.postRequest(axiosInstance, "/users/loginWithWorkflow", {
        referenceId: referenceId,
        organization: organization,
        code: uuidv4()
    }).then(response => {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + _.get(response, 'accessToken')
        return response;
    });
};

var loggedInWithWorkflow = function() {
    return axiosHelper.getRequest(axiosInstance, "/users/loggedInWithWorkflow");
};

var logout = function() {
    return axiosHelper.postRequest(axiosInstance, "/users/logout");
};

var createRecord = function(name, questionsetName, description, requestId, referenceId, organization) {
    let body = {
        name: name,
        questionsetName: questionsetName
    };

    if (description) {
        body.description = description;
    }
    if (requestId) {
        body.requestId = requestId;
    }
    if (referenceId) {
        body.id = referenceId;
    }
    if (organization) {
        body.organization = organization;
    }

    return axiosHelper.postRequest(axiosInstance, "/records", body);
};

var updateRecord = function(data) {
    return axiosHelper.patchRequest(axiosInstance, "/records", data);
};

var getRecordWithRequestId = function(requestId) {
    return axiosHelper.getRequest(axiosInstance, "/records?requestId=" + requestId);
};

var getRecord = function(referenceId) {
    return axiosHelper.getRequest(axiosInstance, "/records/" + referenceId);
};

var commitAssets = function(assetData) {
    return axiosHelper.postRequest(axiosInstance, "/assets", assetData);
};

var uploadMediaMetadata = function(mediaMetadata) {
    return axiosHelper.postRequest(axiosInstance, "/mediametadata", mediaMetadata);
};

var removeMediaMetadata = function(uuidsToRemove, state) {
    return axiosHelper.deleteRequest(axiosInstance, "/mediametadata",{
        workflowSessionId: state.workflowSession.workflowSessionId,
        uuidsToRemove: uuidsToRemove
    });
};

var getWorkflowData = function(workflowDataId) {
    return axiosHelper.getRequest(axiosInstance, "/workflowData/" + workflowDataId);
};

var uploadWorkflowData = function(workflowData, recordId, state) {
    let payload = {
        workflowData: workflowData,
        forwardAnswers: state.forwardAnswers,
        questionSetRefId: state.questionSet.referenceId,
        questionSetVersion: state.questionSet.version,
        recordId: recordId,
        requestId: state.requestId,
        workflowSessionId: state.companionWorkflowSessionId ? state.companionWorkflowSessionId : state.workflowSession.workflowSessionId
    };
    let publicKey = localStorage.getItem("attestivPUBLICKey");
    console.log("attestivPUBLICKey: " + publicKey);
    if (publicKey) {
        payload.publicKey = publicKey;
        localStorage.removeItem("attestivPUBLICKey");
    }
    return axiosHelper.postRequest(axiosInstance, "/workflowData", payload);
};

var removeWorkflowData = function(questionIdsToRemove, state) {
    return axiosHelper.deleteRequest(axiosInstance, "/workflowData",{
        workflowSessionId: state.workflowSession.workflowSessionId,
        questionSetRefId: state.questionSet.referenceId,
        questionIdsToRemove: questionIdsToRemove
    });
};

var createWorkflowSession = function(state, currentQuestionId = "--NEW--", deviceInfo) {
    let body = {
        questionSetId: state.referenceId,
        currentQuestionId: currentQuestionId,
        deviceInfo: deviceInfo,
        recordId: state.recordId
    };

    console.log(body);
    return axiosHelper.postRequest(axiosInstance, "/workflowsession", body);
};

var updateWorkflowSession = function(state, currentQuestionId, recordId, logs, complete = false) {
    let body = {
        workflowSessionId: state.workflowSession.workflowSessionId,
        questionSetId: state.referenceId
    };

    if (currentQuestionId) {
        body.currentQuestionId = currentQuestionId;
    }
    if (recordId) {
        body.recordId = recordId;
    }
    if (logs) {
        body.logs = logs;
    }
    if (complete) {
        body.currentQuestionId = "--DONE--";
    }

    console.log(body);
    return axiosHelper.patchRequest(axiosInstance, "/workflowsession", body);
};

var getWorkflowSession = function(workflowSessionId) {
    return axiosHelper.getRequest(axiosInstance, "/workflowsession/" + workflowSessionId);
};

var uploadMedia = function(file, recordId, mediaUUID, analysis, triageType, models) {
    console.debug("Uploading: " + file.name + " Size: " + file.size);

    uploadProgress.totalToLoad += file.size;
    console.debug("totalToLoad: " + uploadProgress.totalToLoad);

    var formData = new FormData();
    formData.append("recordId", recordId);
    formData.append("uuid", mediaUUID);
    formData.append("media", file);

    var config = {
        headers: {
            "Content-Type": "multipart/form-data"
        }
    };

    let url = "/files";
    if (!_.isEmpty(analysis)) {
        const argusAnalysis = getArgusAnalysis(analysis);
        const analysisTypes = getAnalysisTypes(analysis);
        const requestedAnalysis = argusAnalysis ? ["argus"] : analysisTypes;
        if (_.includes(requestedAnalysis, "argus")) {
            console.debug(`Requesting server-side Argus analysis for media ${mediaUUID} in record ${recordId}`);
        }
        url = "/files?runAnalysis=" + requestedAnalysis.join(",");
        if (_.isObject(argusAnalysis) && argusAnalysis.flags) {
            url += "&argusFlags=" + encodeURIComponent(argusAnalysis.flags);
        }
        if (triageType) {
            url += "&triageType=" + triageType;
        }
        if (models) {
            for (let m of models) {
                url += "&models=" + m;
            }
        }
    }

    return axiosHelper.postRequest(axiosInstance, url, formData, config);
};

var getUIConfig = function() {
    return axiosHelper.getRequest(axiosInstance, "/organizations/ui-config");
}

var markAsStarted = function(requestId) {
    return axiosHelper.patchRequest(axiosInstance, `/clientrequest/${requestId}`,
        {
            state: 'STARTED'
        }
    );
};

var markAsCompleted = function(requestId, recordId) {
    return axiosHelper.patchRequest(axiosInstance, `/clientrequest/${requestId}`,
        {
            state: 'COMPLETED',
            recordId: recordId
        }
    );
};

var getQuestionSet = function(referenceId, organization, referrer) {
    return axiosHelper.getRequest(
        axiosInstance,
        "/questionsets/" +
            referenceId +
            (organization ? "?org=" + organization + (referrer ? "&ref=" + referrer : "") : "")
    );
};

var getClientRequest = function(requestId) {
    return axiosHelper.getRequest(axiosInstance, "/clientrequest/" + requestId);
};

var sendMessage = function(email, smsNumber, message, subject, organization, clientRequestId) {
    let body = {
        message: message,
    };

    if (organization) {
        body.organization = organization;
    }
    if (clientRequestId) {
        body.clientRequestRefId = clientRequestId;
    }
    if (email) {
        body.email = email;
    }
    if (smsNumber) {
        body.smsNumber = smsNumber;
    }
    if (subject) {
        body.subject = subject;
    }

    return axiosHelper.postRequest(axiosInstance, "/messaging/send", body);
};

var sendRequestCompletionEmail = function(clientRequest, questionSet, recordName) {
    let body;

    if (clientRequest) {
        body = {
            firstName: clientRequest.firstName,
            lastName: clientRequest.lastName,
            questionSet: questionSet.name,
            email: clientRequest.email
        };
        if (!_.isEmpty(clientRequest.initiatedBy)) {
            body.initiatedBy = clientRequest.initiatedBy;
        }
    } else {
        body = {
            organization: questionSet.organization
        };
    }

    if (!_.isEmpty(recordName)) {
        body.recordName = recordName;
    }

    return axiosHelper.postRequest(axiosInstance, "/messaging/workflowCompletionEmail", body);
};

var deleteRecordById = function(recordId) {
    return axiosHelper.deleteRequest(axiosInstance, `/records/${recordId}`);
}

var getHealthInfo = function() {
    return axiosHelper.getRequest(axiosInstance, "/health", true); // /health does not have an api version. Third arg forces use of the base serverUrl and not serverUri
}

// Everything below is the former AttestivMediaAnalysis. These are the functions and supporting
// functions for calling forensics.

// Only used for executeDocumentScan, forensic calls use executeModel
// We should merge these calls
function analyzeImage(apiConfig, file, bodyArgs) {
    var formData = new FormData();
    const param = apiConfig.baseUrl === weberUrl ? "file" : "image";
    formData.append(param, file);

    // Add any extra body params to the formdata
    for (let arg in bodyArgs) {
        formData.append(arg, JSON.stringify(bodyArgs[arg]))
    }
    let url = apiConfig.baseUrl + "/" + apiConfig.endpoint;
    let config = {timeout: apiConfig.timeout || 180000};

    return axiosHelper.postRequest(axiosInstance, url, formData, config, true);
};

var executeDocumentScan = function(analysisConfig, file, question, store) {
    const mediaArgs = question.specifierOptions || {};

    return analyzeImage(analysisConfig, file, mediaArgs)
        .then(json => {
            store.dispatch({ type: C.DISPLAY_USER_MESSAGE, message: strings.getString("mediaCaptured"), textColor: "white", backgroundColor: "green" });
            setTimeout(() => {
                store.dispatch({ type: C.HIDE_USER_MESSAGE });
            }, 5000);

            // Soon all weber calls will be under documentScan
            // Until then registration and decpage use the old route
            if (question.questionSpecifier === "documentScan") {
                // Quick and dirty check for an unsupported document error from Weber
                // Stop advancing and alert the user that we can't do this document type
                if (json.fields && json.fields.error && json.fields.error === "This document type is not supported") {
                    openAlertDialog(store, strings.getString("documentTypeNotSupported"));
                    setRepeatQuestion(store, true);
                } else {
                    // Otherwise let us advance
                    setRepeatQuestion(store, false);
                }

                store.dispatch({
                    type: C.ASSIGN_FROM_CAMERA_NEW,
                    cameraFields: question.onCapture.cameraFields,
                    cameraResults: json
                });
            }
            else {
                store.dispatch({
                    type: C.ASSIGN_FROM_CAMERA,
                    cameraFields: question.onCapture.cameraFields,
                    cameraResults: json
                });
            }
        })
        .catch(err => {
            console.error(err);
            // Just make the fields blank - the user will have to fill in
            store.dispatch({
                type: C.ASSIGN_FROM_CAMERA,
                cameraFields: question.onCapture.cameraFields,
                cameraResults: {}
            });
        });
}

// This currently assumes a call to poppernaut
var executeModel = function(store, file, modelConfig, question, otherArgs, answerIndex, isRunInBackground = false, customMetricsTags) {
    var formData = new FormData();
    var param = modelConfig.baseUrl === weberUrl ? "file" : "image";
    param = modelConfig.baseUrl + "/" + modelConfig.endpoint === detectAsyncUrl ? "media" : param;

    if (modelConfig.baseUrl + "/" +  modelConfig.endpoint === detectFacesUrl) {
        let state = store.getState();
        formData.append("image1", file);
        // get the image from the previous question
        formData.append("image2", state.questionHistory[state.currentAnswerIndex - 1].file);
    }
    else {
        formData.append(param, file);
    }

    let url = modelConfig.baseUrl + "/" + modelConfig.endpoint;
    let config = {
        timeout: modelConfig.timeout || 180000
    };

    console.debug("Running analysis: " + modelConfig.endpoint);

    // Add any arguments from the config
    let configArgs = _.get(modelConfig, "arguments", {});
    for (let arg in configArgs) {
        formData.append(arg, configArgs[arg])
    }

    // Throw in any other arguments
    for (let arg in otherArgs) {
        formData.append(arg, otherArgs[arg])
    }

    // add custom tags for metrics
    if (customMetricsTags && !_.isEmpty(customMetricsTags)){
        for (let customTag of customMetricsTags) {
            formData.append("customTags", customTag);
        }
    }

    if (isRunInBackground) {
        store.dispatch({type: C.INCREMENT_BACKGROUND_REQUEST_COUNTER});
        updateProgressTotals(backgroundTaskSize);

        for (let i = 0; i < uploadProgress.totals.length; i++) {
            let t = uploadProgress.totals[i];
            if (t.total === backgroundTaskSize && t.loaded !== backgroundTaskSize) {
                t.loaded += parseInt(backgroundTaskSize * 0.1);
            }
        }
        uploadProgress.totalLoaded += parseInt(backgroundTaskSize * 0.1);
        updateUploadProgress(parseInt(backgroundTaskSize * 0.1), backgroundTaskSize);
    }

    return axiosHelper.postRequest(axiosInstance, url, formData, config, true)
        .then(response => {
            if (isRunInBackground) {
                for (let i = 0; i < uploadProgress.totals.length; i++) {
                    let t = uploadProgress.totals[i];
                    if (t.total === backgroundTaskSize && t.loaded <= backgroundTaskSize) {
                        t.loaded += (backgroundTaskSize - parseInt(backgroundTaskSize * 0.1));
                    }
                }
                uploadProgress.totalLoaded += (backgroundTaskSize - parseInt(backgroundTaskSize * 0.1));
                updateUploadProgress(backgroundTaskSize - parseInt(backgroundTaskSize * 0.1), backgroundTaskSize);
                store.dispatch({type: C.DECREMENT_BACKGROUND_REQUEST_COUNTER});
            }
            return handleAnalysisResponse(store, modelConfig, file, question, answerIndex, response);
        })
        .catch(err => {
            console.error("Error occurred while trying to execute forensic model.");
            console.error(err)
            return handleAnalysisResponse(store, modelConfig, file, question, answerIndex,
                [
                    {
                        "detect_tampering_result": {
                            error: err.message
                        },
                        "detect_objects_result": {
                            error: err.message
                        }
                    }
                ]);
        });
}

// Helper function to get the score for a specific model
// If poppernaut did not give us that model, consider it a 0 (not tampered)
function getTamperScore(response, model) {
    for (let assessment of _.get(response, "assessments", [])) {
        if (assessment.model === model) {
            return assessment.compromisedScore;
        }
    }

    return 0;
}

// Use the callback field in the imageAnalysis config to tell us what to do
function handleAnalysisResponse(store, modelConfig, file, question, answerIndex, response) {
    let callback = _.get(modelConfig, "callback", "");

    console.debug("Handling response: " + modelConfig.endpoint);

    switch (callback) {
        case "handleDetectResult": handleDetectResult(store, modelConfig, file, question, answerIndex, response); break;
        case "handleTriageResult": handleTriageResult(store, modelConfig, file, question, answerIndex, response); break;
        case "handleObjectDetectionResult": handleObjectDetectionResult(store, question, answerIndex, response); break;
        case "handleFacialDetectionResult": handleFacialDetectionResult(store, question, answerIndex, response); break;
        case "handleExtractResult": handleExtractResult(store, question, response); break;
        case "handleDecPageResult": handleDecPageResult(store, question, response); break;
        case "handleResultAssignment": handleResultAssignment(store, question, response); break;
        case "handleDamageDetectionResult": handleDamageDetectionResult(store, response); break;

        // Our debug logic
        case "handlePopCollection": handlePopCollection(store, response); break;
        case "handleDebugTriageResult": handleDebugTriageResult(store, modelConfig, file, response); break;
        default: console.error("No analysis callback specified!");
    }
}

function handlePopCollection(store, response) {
    // The ingest endpoint just returns a "value"
    let value = _.get(response, "value");
    let correct = _.get(response, "correct");

    let message;
    if (correct) {
        message = "The model guessed right";
    } else {
        message = "The model guessed wrong";
    }

    message += ` (${value})`

    openAlertDialog(store, message);
}

function handleDebugTriageResult(store, tamperConfig, file, response) {
    let threshold = tamperConfig.tamperScoreThreshold;
    let score = response.tamperScore;

    let responseStr = "";
    // Just build a string with all the scores
    for (let assessment of response.assessments) {
        responseStr += `-- ${assessment.model}: ${assessment.compromisedScore} --`
    }

    // If the total score is high enough, use the details to return a specific error message
    if (score >= threshold) {
        openAlertDialog(store, "Photo is TAMPERED: " + responseStr);
    } else {
        openAlertDialog(store, "Photo is CLEAN: " + responseStr);
    }
}

function handleObjectDetectionResult(store, question, answerIndex, response) {
    store.dispatch({type: C.STORE_ANALYSIS_RESPONSE, key: 'objectsDetectedResult', questionID: question.questionID, answerIndex: answerIndex, response: response.objects});
}

function handleFacialDetectionResult(store, question, answerIndex, response) {
    let json = {
        "facialMatch": _.get(response, "facesMatch") === "true" ? "Yes - " + response.confidence.toFixed(2) + "% similar" : "No"
    };
    store.dispatch({type: C.ASSIGN_FROM_CAMERA_NEW, cameraFields: question.onCapture.cameraFields, cameraResults: {"fields": json}});
    store.dispatch({type: C.RECORD_BILLABLE_OPERATION, operation: "facialDetection", count: 1});
    store.dispatch({type: C.STORE_ANALYSIS_RESPONSE, key: 'facialDetectionResult', questionID: question.questionID, answerIndex: answerIndex, response: response});
}

function handleDamageDetectionResult(store, response) {
    store.dispatch({type: C.STORE_ANALYSIS_RESPONSE, key: 'damageDetectedResult', response: response.results});
}

function isFacialDetectionQuestion(question) {
    let result = _.filter(_.get(question, "analysis", []), analysis => analysis === "facialDetection");
    return result.length > 0;
}

function isLicensePictureQuestion(question) {
    return _.includes(_.get(question, "questionLabel", ""), "driver's license")
}

function isBlankExtractValue(value) {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        return value.trim().length === 0;
    }
    return false;
}

function handleExtractResult(store, question, response) {
    const results = _.get(response, ["detect_expenses_result", "expenses", "summary"], _.get(response, ["0", "detect_expenses_result", "expenses", "summary"], {}));
    
    // special case handling with name extaction
    let name = _.get(results, "name");
    if (isBlankExtractValue(name)) {
        name = _.get(results, "vendor_name");
        if (!isBlankExtractValue(name)) {
            results.name = name;
        }
    }    

    // special case handling with address extaction
    let address = _.get(results, "address");
    if (isBlankExtractValue(address)) {
        address = _.get(results, "vendor_address");
        if (!isBlankExtractValue(address)) {
            results.address = address;
        }
    }    

    // special case handling with phone extaction
    let phone = _.get(results, "telephone");
    if (isBlankExtractValue(phone)) {
        phone = _.get(results, "vendor_phone");
        if (!isBlankExtractValue(phone)) {
            results.phone = phone;
        }
    }    

    // special case handling with total extaction
    let total = _.get(results, "total");
    if (isBlankExtractValue(total)) {
        _.each(_.keys(results), key => {
            if (key.startsWith("invoice_") && key.endsWith("_total")) {
                total = _.get(results, key);
                if (!isBlankExtractValue(total)) {
                    results.total = total;
                }
            }
        });
    }    

    // special case handling with sales tax amount extaction
    let salesTax = _.get(results, "tax");
    if (isBlankExtractValue(salesTax)) {
        salesTax = _.get(results, "sales_tax");
        results.sales_tax = _.get(results, "sales_tax_" + salesTax, salesTax);
    }
    else {
        results.sales_tax = salesTax;
    }

    // special case handling with date extaction
    let date = _.trim(_.get(results, "date"));
    if (isBlankExtractValue(date)) {
        date = _.get(results, "invoice_receipt_date");
        if (!isBlankExtractValue(date)) {
            results.date = date;
        }
    }

    // Map extracted fields to receipt question cameraFields (MerchantName, MerchantAddress, etc.)
    if (isBlankExtractValue(_.get(results, "MerchantName")) && !isBlankExtractValue(_.get(results, "name"))) {
        results.MerchantName = results.name;
    }
    if (isBlankExtractValue(_.get(results, "MerchantAddress")) && !isBlankExtractValue(_.get(results, "address"))) {
        results.MerchantAddress = results.address;
    }
    if (isBlankExtractValue(_.get(results, "MerchantPhoneNumber")) && !isBlankExtractValue(_.get(results, "telephone"))) {
        results.MerchantPhoneNumber = results.telephone;
    }
    if (isBlankExtractValue(_.get(results, "TransactionDate")) && !isBlankExtractValue(_.get(results, "date"))) {
        results.TransactionDate = results.date;
    }
    if (isBlankExtractValue(_.get(results, "Subtotal")) && !isBlankExtractValue(_.get(results, "subtotal"))) {
        results.Subtotal = results.subtotal;
    }
    if (isBlankExtractValue(_.get(results, "Tax"))) {
        const taxValue = _.get(results, "sales_tax") || _.get(results, "tax");
        if (!isBlankExtractValue(taxValue)) {
            results.Tax = taxValue;
        }
    }
    if (isBlankExtractValue(_.get(results, "Total")) && !isBlankExtractValue(_.get(results, "total"))) {
        results.Total = results.total;
    }
    if (isBlankExtractValue(_.get(results, "Items")) && !isBlankExtractValue(_.get(results, "line_items"))) {
        results.Items = results.line_items;
    }

    store.dispatch({
        type: C.STORE_EXTRACTED_RESULT,
        cameraFields: question.onCapture.cameraFields,
        cameraResults: results
    });
    store.dispatch({
        type: C.RECORD_BILLABLE_OPERATION,
        operation: "textExtraction",
        count: 1
    });
};

function handleDecPageResult(store, question, response) {
    // normalize response as it has value and confidence, and coverages (e.g. dwelling -> coverage_a)
    const fields = _.get(response, 'fields', {});
    const fieldValues = _.mapValues(fields, value => _.get(value, 'value'));
    const json = _.mapKeys(fieldValues, (value, key) => {
        switch (key) {
            case 'full_name': return 'name';
            case 'email_address': return 'email';
            case 'policy_number': return 'policy';
            case 'total_premium': return 'premium';
            case 'dwelling': return 'coverage_a';
            case 'additionalStructure': return 'coverage_b';
            case 'personalProperty': return 'coverage_c';
            case 'additionalLivingExpense': return 'coverage_d';
            case 'personalLiability': return 'coverage_e';
            case 'medicalPayments': return 'coverage_f';
            default: return key;
        }
    });
    return handleResultAssignment(store, question, json);
}

function handleResultAssignment(store, question, response) {
    const json = response;
    store.dispatch({
        type: C.ASSIGN_FROM_CAMERA,
        cameraFields: question.onCapture.cameraFields,
        cameraResults: json
    });
};

function handleDetectResult(store, tamperConfig, file, question, answerIndex, response) {
    handleTriageResult(store, tamperConfig, file, question, answerIndex, response[0].detect_tampering_result);
    handleObjectDetectionResult(store, question, answerIndex, response[0].detect_objects_result);
}

function handleTriageResult(store, tamperConfig, file, question, answerIndex, response) {
    let threshold = tamperConfig.tamperScoreThreshold;
    let score = response.tamperScore;
    let currentQuestionRetries = store.getState().currentQuestionRetries;

    store.dispatch({type: C.STORE_ANALYSIS_RESPONSE, key: 'tamperedResult', questionID: question.questionID, answerIndex: answerIndex, response: response});

    // If the total score is high enough, use the details to return a specific error message
    if (score >= threshold) {
        // If it's a pop or downloaded photo, prompt the user to retake
        store.dispatch({type: C.UPDATE_QUESTION_RETRIES, count: currentQuestionRetries + 1});
        let shouldRetry = currentQuestionRetries < tamperConfig.maxRetryRequests || tamperConfig.maxRetryRequests === -1;
        if (shouldRetry) {
            openAlertDialog(store, (getTamperScore(response, "pop") >= threshold || getTamperScore(response, "downloads") >= threshold)
                        ? strings.getString("retakePhoto")
                        : strings.getString("ensureOriginalPhoto"));
        }

        addMediaTags(store, file.uuid, ["recapturedPhoto"]);
        store.dispatch({type: C.MARK_MEDIA_AS_TAMPERED, questionID: question.questionID, answerIndex: answerIndex});

        return setRepeatQuestion(store, shouldRetry);
    }
    else {
        return setRepeatQuestion(store, false)
    }
}

function openAlertDialog(store, text) {
    store.dispatch({
        type: C.SHOW_DIALOG,
        text: text
    });
}

function setRepeatQuestion(store, shouldRepeat) {
    let retries = store.getState().currentQuestionRetries;

    // Reset or increment the counter
    store.dispatch({
        type: C.UPDATE_QUESTION_RETRIES,
        count: shouldRepeat ? retries + 1: 0
    });

    // Set to advance or not
    store.dispatch({
        type: C.CAN_ADVANCE,
        canAdvance: !shouldRepeat
    });
}

function addMediaTags(store, mediaUuid, tagsList) {
    store.dispatch({ type: C.APPEND_MEDIA_FILE_DATA, uuid: mediaUuid, data: { tags: tagsList }});
}

export default {
    // AttestivMediaAnalysis exposed functions
    executeModel,
    executeDocumentScan,

    // AttestivNet exposed functions
    loginWithRequestId,
    loggedInWithRequestId,
    loginWithWorkflow,
    loggedInWithWorkflow,
    logout,
    createRecord,
    getRecordWithRequestId,
    getRecord,
    updateRecord,
    getWorkflowData,
    uploadWorkflowData,
    removeWorkflowData,
    commitAssets,
    uploadMediaMetadata,
    removeMediaMetadata,
    uploadMedia,
    getUIConfig,
    markAsStarted,
    markAsCompleted,
    createWorkflowSession,
    updateWorkflowSession,
    getWorkflowSession,
    getQuestionSet,
    getClientRequest,
    setUploadProgressFunction,
    sendMessage,
    sendRequestCompletionEmail,
    deleteRecordById,
    getHealthInfo,
    isFacialDetectionQuestion,
    isLicensePictureQuestion
};
